import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, formsApi, bullhornApi, tenantsApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import UsageBadge from '../components/UsageBadge';
import { toast } from 'sonner';
import {
  FileText, Send, CheckCircle2, Eye, RefreshCw, Link2, ArrowRight,
  ClipboardEdit, Inbox, TrendingUp, Mail, ClipboardList,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

/**
 * UnifiedDashboardPage — single, workspace-agnostic overview.
 *
 * Replaces the older split DashboardPage / FormsDashboardPage rendering.
 * The page is reachable from the persistent Dashboard nav link (now
 * positioned ABOVE the workspace pill switcher in the sidebar) so the
 * recruiter always lands on the same overarching view regardless of
 * which workspace they were last in.
 *
 * Layout:
 *   1. Page header (no Bullhorn Sync card per product feedback — that
 *      noise is moved to a small inline connection chip).
 *   2. DOCUMENTS section — clearly labelled with a header band, 4 KPIs
 *      (Total / Sent / Opened / Completed) + quick-action tiles.
 *   3. FORMS section (only when ``enable_online_forms`` is on) — same
 *      shape, fuchsia/violet accent, KPIs (Total Forms / Submissions /
 *      Completed / Completion Rate) + completion-status row.
 *   4. Merged Recent Activity feed (documents + form submissions
 *      interleaved by timestamp, each row tagged with its source).
 */

const STATUS_LABELS = {
  sent: 'Sent', opened: 'Opened', in_progress: 'In progress',
  completed: 'Completed', expired: 'Expired', cancelled: 'Cancelled',
};
const STATUS_CLASSES = {
  sent: 'bg-sky-100 text-sky-700',
  opened: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-slate-100 text-slate-700',
};

export default function UnifiedDashboardPage() {
  const navigate = useNavigate();
  const { tenant, getTenantPath } = useAuth();
  const docsEnabled = tenant?.settings?.enable_documents !== false;
  const formsEnabled = !!tenant?.settings?.enable_online_forms;

  const [docStats, setDocStats] = useState(null);
  const [formStats, setFormStats] = useState(null);
  const [bhConnected, setBhConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Fetch in parallel — each endpoint guarded by its own product
      // feature flag so disabled products simply resolve to null.
      const tasks = [
        docsEnabled ? dashboardApi.getStats() : Promise.resolve({ data: null }),
        formsEnabled ? formsApi.dashboardStats() : Promise.resolve({ data: null }),
        bullhornApi.status().catch(() => ({ data: { connected: false } })),
      ];
      const [docsRes, formsRes, bhRes] = await Promise.all(tasks);
      setDocStats(docsRes.data);
      setFormStats(formsRes.data);
      setBhConnected(!!bhRes.data?.connected);

      // Best-effort Bullhorn auto-connect when the chip is red.
      if (!bhRes.data?.connected && tenant?.id) {
        try {
          const auto = await tenantsApi.autoConnectBullhorn(tenant.id);
          if (auto.data?.auto_connected) setBhConnected(true);
        } catch (_e) { console.warn('Bullhorn auto-connect skipped:', _e?.message || _e); }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [docsEnabled, formsEnabled, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  // Merge document + form activity into one chronologically-sorted feed.
  const recent = useMemo(() => {
    const items = [];
    (docStats?.recent_activity || []).forEach((d) => items.push({
      source: 'document',
      id: `doc-${d.id}`,
      title: d.candidate_name,
      subtitle: d.template_name,
      status: d.status,
      ts: d.sent_at,
      onClick: () => navigate(getTenantPath('/documents')),
    }));
    (formStats?.recent_activity || []).forEach((s) => items.push({
      source: 'form',
      id: `form-${s.id}`,
      title: s.candidate_name,
      subtitle: s.form_name,
      status: s.status,
      ts: s.sent_at,
      onClick: () => navigate(getTenantPath('/forms/submissions')),
    }));
    items.sort((a, b) => {
      if (!a.ts) return 1;
      if (!b.ts) return -1;
      return new Date(b.ts) - new Date(a.ts);
    });
    return items.slice(0, 12);
  }, [docStats, formStats, navigate, getTenantPath]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const totalDocs = docStats?.documents?.total || 0;
  const totalForms = formStats?.forms?.total || 0;

  return (
    <DashboardLayout>
      <div className="space-y-10 p-8 max-w-7xl mx-auto" data-testid="unified-dashboard-page">

        {/* ============== HEADER ============== */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground font-body mt-1">
              {docsEnabled && formsEnabled
                ? 'Overview of your document and form workflows.'
                : docsEnabled
                ? 'Overview of your document workflow.'
                : 'Overview of your form workflow.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <UsageBadge />
            <BullhornChip connected={bhConnected} onConnect={async () => {
              try {
                await bullhornApi.authenticate();
                setBhConnected(true);
                toast.success('Connected to Bullhorn');
              } catch (_) {
                toast.error('Failed to connect to Bullhorn');
              }
            }} />
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={refreshing}
              className="gap-1.5"
              data-testid="dashboard-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* ============== DOCUMENTS SECTION ============== */}
        {docsEnabled && (
          <section data-testid="dashboard-documents-section">
            <SectionHeader
              accent="from-[#f97316] to-[#f59e0b]"
              icon={FileText}
              tag="DOCUMENTS"
              tagClass="text-orange-700 bg-orange-100"
              title="PDF documents"
              subtitle="Signed templates, contracts and onboarding packs."
              ctaLabel="Manage templates"
              onCta={() => navigate(getTenantPath('/templates'))}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <StatCard
                icon={FileText}
                label="Total documents"
                value={totalDocs}
                color="bg-gradient-to-br from-[#1e293b] to-[#334155]"
                onClick={() => navigate(getTenantPath('/documents'))}
                testId="stat-doc-total"
              />
              <StatCard
                icon={Send}
                label="Sent"
                value={docStats?.documents?.sent || 0}
                color="bg-gradient-to-br from-[#3b82f6] to-[#2563eb]"
                onClick={() => navigate(getTenantPath('/documents?status=sent'))}
                testId="stat-doc-sent"
              />
              <StatCard
                icon={Eye}
                label="Opened"
                value={docStats?.documents?.opened || 0}
                color="bg-gradient-to-br from-[#f97316] to-[#f59e0b]"
                onClick={() => navigate(getTenantPath('/documents?status=opened'))}
                testId="stat-doc-opened"
              />
              <StatCard
                icon={CheckCircle2}
                label="Completed"
                value={docStats?.documents?.completed || 0}
                color="bg-gradient-to-br from-[#10b981] to-[#059669]"
                onClick={() => navigate(getTenantPath('/documents?status=completed'))}
                testId="stat-doc-completed"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <QuickAction
                icon={Send}
                title="Send a document"
                body="Send a template to a candidate or contact."
                onClick={() => navigate(getTenantPath('/send'))}
                gradient="from-[#f97316] to-[#f59e0b]"
                testId="quick-doc-send"
              />
              <QuickAction
                icon={ClipboardList}
                title="View all documents"
                body="Track status, resend reminders, manage in bulk."
                onClick={() => navigate(getTenantPath('/documents'))}
                gradient="from-[#1e293b] to-[#334155]"
                testId="quick-doc-list"
              />
            </div>
          </section>
        )}

        {/* ============== FORMS SECTION ============== */}
        {formsEnabled && (
          <section data-testid="dashboard-forms-section">
            <SectionHeader
              accent="from-violet-500 to-indigo-500"
              icon={ClipboardEdit}
              tag="ONLINE FORMS"
              tagClass="text-violet-700 bg-violet-100"
              title="Online forms"
              subtitle="Drag-and-drop forms for intake, references and compliance."
              ctaLabel="Manage forms"
              onCta={() => navigate(getTenantPath('/forms'))}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <StatCard
                icon={ClipboardEdit}
                label="Total forms"
                value={totalForms}
                color="bg-gradient-to-br from-violet-500 to-fuchsia-500"
                onClick={() => navigate(getTenantPath('/forms'))}
                testId="stat-form-total"
              />
              <StatCard
                icon={Inbox}
                label="Submissions"
                value={formStats?.submissions?.total || 0}
                color="bg-gradient-to-br from-sky-500 to-blue-600"
                onClick={() => navigate(getTenantPath('/forms/submissions'))}
                testId="stat-form-submissions"
              />
              <StatCard
                icon={CheckCircle2}
                label="Completed"
                value={formStats?.submissions?.completed || 0}
                color="bg-gradient-to-br from-emerald-500 to-teal-600"
                onClick={() => navigate(getTenantPath('/forms/submissions') + '?status=completed')}
                testId="stat-form-completed"
              />
              <StatCard
                icon={TrendingUp}
                label="Completion rate"
                value={`${formStats?.completion_rate ?? 0}%`}
                color="bg-gradient-to-br from-amber-500 to-orange-500"
                testId="stat-form-completion-rate"
              />
            </div>

            {/* Top forms strip — only if data present */}
            {formStats?.top_forms?.length ? (
              <div className="mt-4 neumorphic rounded-xl p-6" data-testid="dashboard-top-forms">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-base font-semibold">Top forms by submission volume</h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate(getTenantPath('/forms'))} className="gap-1.5">
                    View all <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {formStats.top_forms.slice(0, 5).map((f, idx) => (
                    <button
                      key={f.form_id}
                      type="button"
                      onClick={() => navigate(getTenantPath(`/forms/${f.form_id}/submissions`))}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                      data-testid={`dashboard-top-form-${idx}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-mono text-muted-foreground w-5">#{idx + 1}</span>
                        <span className="font-medium text-sm truncate">{f.form_name}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">{f.count} submission{f.count === 1 ? '' : 's'}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        )}

        {/* ============== MERGED RECENT ACTIVITY ============== */}
        <section className="neumorphic rounded-xl p-6" data-testid="dashboard-recent-activity">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Recent activity</h3>
            <p className="text-xs text-muted-foreground">Latest 12 sends across both products</p>
          </div>
          {recent.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-muted-foreground">No activity yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recent.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={item.onClick}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-muted/30 hover:bg-muted transition-colors text-left"
                  data-testid={`dashboard-activity-${item.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <SourceBadge source={item.source} />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StatusPill status={item.status} />
                    <span className="text-xs text-muted-foreground w-24 text-right">
                      {item.ts ? formatRelative(item.ts) : '—'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

/* ---------- Helpers ---------- */

function SectionHeader({ accent, icon: Icon, tag, tagClass, title, subtitle, ctaLabel, onCta }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-sm`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${tagClass}`}>{tag}</span>
            <h2 className="font-heading text-xl font-bold text-foreground">{title}</h2>
          </div>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {ctaLabel && (
        <Button variant="ghost" size="sm" onClick={onCta} className="gap-1.5 text-foreground">
          {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, onClick, testId }) {
  return (
    <div
      className={`neumorphic rounded-xl p-5 ${onClick ? 'cursor-pointer card-hover transition-transform hover:-translate-y-0.5' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="overline mb-2">{label}</p>
          <p className="font-heading text-3xl font-bold text-foreground">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, title, body, onClick, gradient, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="neumorphic rounded-xl p-5 card-hover cursor-pointer text-left transition-transform hover:-translate-y-0.5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground" />
      </div>
      <h3 className="font-heading font-semibold text-base mb-0.5">{title}</h3>
      <p className="text-xs text-muted-foreground">{body}</p>
    </button>
  );
}

function SourceBadge({ source }) {
  if (source === 'form') {
    return (
      <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0" title="Online form">
        <ClipboardEdit className="w-4 h-4 text-violet-600" />
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0" title="Document">
      <FileText className="w-4 h-4 text-orange-600" />
    </div>
  );
}

function StatusPill({ status }) {
  const cls = STATUS_CLASSES[status] || STATUS_CLASSES.sent;
  const lbl = STATUS_LABELS[status] || status;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {lbl}
    </span>
  );
}

function BullhornChip({ connected, onConnect }) {
  if (connected) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-xs font-medium text-emerald-700" data-testid="bullhorn-connected-badge">Bullhorn Connected</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-full border border-red-200">
        <div className="w-2 h-2 rounded-full bg-red-500" />
        <span className="text-xs font-medium text-red-700" data-testid="bullhorn-disconnected-badge">Bullhorn Not Connected</span>
      </div>
      <Button size="sm" variant="outline" onClick={onConnect} className="gap-1.5" data-testid="connect-bullhorn-button">
        <Link2 className="w-3.5 h-3.5" />
        Connect
      </Button>
    </div>
  );
}

function formatRelative(ts) {
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return format(new Date(ts), 'MMM d');
  }
}
